'use client';

import { useEffect, useState } from 'react';
import { ClerkProvider } from '@clerk/nextjs';
import { enUS, esES, frFR, ptPT } from '@clerk/localizations';
import { type Locale, defaultLocale, isSupportedLocale, matchLocale } from '@/i18n/config';

// Clerk's own UI (sign-in / sign-up / user button) is localized via the
// ClerkProvider `localization` prop, NOT next-intl. Map each app locale to the
// matching Clerk pack. 'pt' → European Portuguese.
const CLERK_LOCALIZATIONS: Record<Locale, typeof enUS> = {
  en: enUS,
  es: esES,
  pt: ptPT,
  fr: frFR,
};

// Mirror LocaleProvider's resolution so Clerk shows the same language on first
// paint. Read here (not from LocaleProvider context) because ClerkProvider sits
// ABOVE LocaleProvider in the tree and can't consume its context.
function readActiveLocale(): Locale {
  if (typeof window === 'undefined') return defaultLocale;
  try {
    const stored = localStorage.getItem('locale');
    if (stored && isSupportedLocale(stored)) return stored;
  } catch {
    // ignore
  }
  // Partner-domain default (written by LocaleProvider once the branding query
  // resolves) — only consulted when the user has made no explicit choice.
  try {
    const match = document.cookie.match(/(?:^|;\s*)partner_locale=([^;]+)/);
    const value = match ? decodeURIComponent(match[1]) : null;
    if (value && isSupportedLocale(value)) return value;
  } catch {
    // ignore
  }
  try {
    for (const tag of navigator.languages ?? [navigator.language]) {
      const matched = matchLocale(tag);
      if (matched) return matched;
    }
  } catch {
    // ignore
  }
  return defaultLocale;
}

/**
 * Wraps ClerkProvider and keeps its `localization` in sync with the active UI
 * locale. Reacts to the `localechange` event that LocaleProvider dispatches when
 * the user switches language (including the switcher on the auth pages), and to
 * the server-adopted locale for signed-in users.
 */
export function ClerkLocalizationProvider({
  children,
  ...clerkProps
}: React.ComponentProps<typeof ClerkProvider>) {
  const [locale, setLocale] = useState<Locale>(defaultLocale);

  useEffect(() => {
    setLocale(readActiveLocale());
    const onChange = (e: Event) => {
      const next = (e as CustomEvent).detail;
      if (typeof next === 'string' && isSupportedLocale(next)) setLocale(next);
    };
    window.addEventListener('localechange', onChange);
    return () => window.removeEventListener('localechange', onChange);
  }, []);

  return (
    <ClerkProvider localization={CLERK_LOCALIZATIONS[locale]} {...clerkProps}>
      {children}
    </ClerkProvider>
  );
}
